import React from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { ArrowLeft } from "@phosphor-icons/react";

const SECTION = "mt-8 first:mt-0";
const H2 = "font-serif-display text-2xl sm:text-3xl text-[#3F2D1B] mb-3 leading-tight";
const P = "text-sm sm:text-base text-[#3F2D1B] leading-relaxed mb-3";
const UL = "list-disc pl-6 space-y-1.5 mb-4 text-sm sm:text-base text-[#3F2D1B]";

/**
 * Privacy Policy — Google Play Data Safety + Apple App Privacy +
 * DPDP Act 2023 + GDPR + CCPA compliant. Every data type listed here
 * MUST also be declared in the Play Console Data Safety form AND the
 * App Store Connect App Privacy form, and vice-versa. Mismatch is the
 * #1 reason Play/App Store reviews reject astrology / lifestyle apps.
 */
export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-5 sm:px-8 py-10 sm:py-14">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted hover:text-[#3F2D1B] mb-6" data-testid="privacy-back">
          <ArrowLeft size={14} /> Back to home
        </Link>

        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-700 font-semibold mb-2">
          AstroKuberChat &middot; Legal
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#3F2D1B] leading-tight">
          Privacy Policy
        </h1>
        <p className="text-xs text-muted mt-3">
          Effective date: 26 February 2026 &middot; Last updated: 26 February 2026
        </p>

        <div className="mt-10 space-y-1" data-testid="privacy-content">

          {/* ── 1. Who we are ────────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>1. Who we are</h2>
            <p className={P}>
              AstroKuberChat (the &ldquo;Service&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) is an
              online Vedic astrology platform that lets you chat with verified human
              and AI astrologers, run premium calculators (Kundali, Navamsa, Doshas,
              Numerology, Past-Life, Nadi, Vimshottari Dasha, 20-Year Forecast and
              27+ others) and access curated astrological reading material on web,
              on Android (via Google Play) and on iOS (via the Apple App Store).
              The Service is operated from India and is accessible at{" "}
              <b>https://astrokuberchat.com</b>, as an Android app published by{" "}
              <b>AstroKuberChat</b> on Google Play, and as the <b>AstroKuberChat</b>{" "}
              iOS app on the Apple App Store.
            </p>
            <p className={P}>
              For any privacy-related question or to exercise the rights described
              below, write to us at{" "}
              <a className="text-amber-700 underline" href="mailto:privacy@astrokuberchat.com">privacy@astrokuberchat.com</a>.
            </p>
          </section>

          {/* ── 2. Data we collect (Play Data Safety parity) ─────── */}
          <section className={SECTION}>
            <h2 className={H2}>2. Information we collect</h2>
            <p className={P}>
              We collect only data necessary to provide the Service. Each item below
              maps directly to the &ldquo;Data Safety&rdquo; declaration on our
              Google Play Store listing and to the &ldquo;App Privacy&rdquo;
              declaration on our Apple App Store listing.
            </p>
            <ul className={UL}>
              <li><b>Personal identifiers</b> &mdash; name, email address, profile picture
                received via Google Sign-In. <i>Required for account creation. No password
                is ever shared with us.</i></li>
              <li><b>Phone number</b> &mdash; collected only if you opt in to receive a
                voice call (PSTN) from an astrologer. Used to bridge the call via
                Twilio.</li>
              <li><b>Birth details</b> &mdash; date, time, place of birth, gender. Used purely
                to compute your chart. Stored against your account so you don&rsquo;t
                have to re-enter them.</li>
              <li><b>In-app messages</b> &mdash; the chat transcripts between you and any
                astrologer (human or AI) on the Service.</li>
              <li><b>Photos</b> &mdash; only when you voluntarily upload a palm photograph
                for our Palmistry tool. Stored encrypted and accessible only to the
                astrologer reviewing your reading.</li>
              <li><b>Payment information</b> &mdash; when you recharge your wallet or
                purchase a paid tool. Card / UPI / netbanking / PayPal account details
                are handled <b>exclusively</b> by Razorpay (INR) or PayPal (USD); we
                only retain a transaction ID, currency, amount and your billing
                country.</li>
              <li><b>Approximate location</b> &mdash; inferred from your IP address (country
                + city, not GPS) to set your default currency (INR vs USD), surface
                geo-relevant promotions and detect fraud.</li>
              <li><b>Device &amp; usage data</b> &mdash; user-agent, browser/OS version,
                screen size, page paths visited, referring URL, click events, time on
                page. Used for analytics, debugging and Google Ads conversion
                attribution.</li>
              <li><b>Crash logs &amp; performance data</b> &mdash; stack traces, error
                identifiers, page where the error occurred. No personal data is
                included in these logs.</li>
              <li><b>Push-notification subscription endpoint</b> &mdash; if you grant
                push permission, we store your push token so we can send you booking
                alerts, astrologer-ready notifications and promo announcements. On
                iOS this is your Apple Push Notification service (APNs) device token;
                on Android this is the Firebase Cloud Messaging (FCM) token; on web
                this is the W3C Push API endpoint + VAPID identifier. You can revoke
                this at any time from your device or browser settings.</li>
              <li><b>Cookies</b> &mdash; a first-party <code>session_token</code> cookie for
                authentication (HttpOnly, Secure, SameSite=None, ~7-day rotation),
                short-lived attribution cookies for marketing analytics, and
                CSRF/anti-bot tokens.</li>
            </ul>
            <p className={P}>
              <b>We do NOT collect:</b> contacts, calendar, microphone (outside an
              active voice call you initiate), camera (outside a palm photo you
              actively upload), files outside the upload picker, SMS, browsing
              history outside our Service, fitness/health sensors, sexual
              orientation, race, ethnicity, political opinions, or religious beliefs
              other than the astrology context you voluntarily share.
            </p>
          </section>

          {/* ── 3. How we use it ─────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>3. How we use your information</h2>
            <ul className={UL}>
              <li>Account creation, sign-in and session management.</li>
              <li>Computing astrological charts, dashas, transits, navamsa, doshas and
                AI-generated interpretations based on your birth details.</li>
              <li>Enabling real-time chat and voice calls with onboarded astrologers.</li>
              <li>Processing payments through Razorpay and PayPal and crediting your
                wallet.</li>
              <li>Sending transactional emails (booking confirmations, receipts,
                magic-link sign-in, password reset, account-deletion confirmation).</li>
              <li>Sending push notifications you have opted in to receive.</li>
              <li>Measuring marketing performance (Google Ads, GA4) and improving
                the Service.</li>
              <li>Fraud prevention, abuse detection and safeguarding the Service
                for all users.</li>
              <li>Complying with applicable laws and responding to lawful requests.</li>
            </ul>
            <p className={P}>
              We do <b>not</b> sell your personal data to third parties. We do <b>not</b>
              use your personal data to train any public AI model.
            </p>
          </section>

          {/* ── 4. AI processing ─────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>4. AI processing of your birth details</h2>
            <p className={P}>
              Several of our calculators (e.g. AI Astrologer chat, Daily Horoscope,
              Cosmic Event Guidance, Palm Reading, Life Replay, Past-Life and 20-Year
              Forecast) pass your computed chart points &mdash; <i>never</i> your name,
              email, phone or address &mdash; to Anthropic Claude and/or Google Gemini
              to generate the written interpretation. These providers process the
              prompt to return the response under their enterprise API terms and do
              <b>not</b> retain the prompt for model training.
            </p>
          </section>

          {/* ── 5. Third parties / sub-processors ────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>5. Sharing with third parties</h2>
            <p className={P}>
              We share data only with the following sub-processors, each strictly
              bound by their own privacy commitments and DPAs we have in place:
            </p>
            <ul className={UL}>
              <li><b>Google LLC</b> &mdash; Google Sign-In, Google Ads conversion tracking,
                Google Analytics 4, Google Play distribution and licensing.</li>
              <li><b>Razorpay Software Pvt. Ltd.</b> &mdash; INR payment processing
                (cards, UPI, netbanking, wallets).</li>
              <li><b>PayPal Holdings, Inc.</b> &mdash; USD payment processing for
                international users.</li>
              <li><b>Twilio Inc.</b> &mdash; PSTN voice-call bridging, SMS OTP delivery
                (where applicable) and call status callbacks. Receives only your
                phone number, the astrologer&rsquo;s phone number, the call timestamp
                and duration.</li>
              <li><b>Anthropic, PBC</b> and <b>Google DeepMind (Gemini)</b> &mdash; LLM
                inference for AI calculators (only the computed chart payload, never
                personal identifiers).</li>
              <li><b>Resend Inc.</b> &mdash; transactional email delivery.</li>
              <li><b>Apple Inc.</b> &mdash; Apple Push Notification service (APNs) for
                delivering iOS push notifications, App Store distribution and crash
                diagnostics you have opted in to share with the developer via iOS
                Settings &rarr; Privacy &amp; Security &rarr; Analytics &amp;
                Improvements.</li>
              <li><b>Web-push providers</b> (FCM/Mozilla autopush) &mdash; delivery of
                Android and browser push notifications you have opted in to.</li>
            </ul>
            <p className={P}>
              We may also disclose information if compelled by a valid legal process
              (court order, statutory demand) issued by a competent authority in
              India or by a foreign authority pursuant to a mutual legal
              assistance treaty.
            </p>
          </section>

          {/* ── 6. Data retention ────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>6. Data retention</h2>
            <p className={P}>
              We retain your account data for as long as your account is active.
              On deletion, all personally-identifying data is wiped within 7 days
              (instantly for self-service deletions). The following exceptions apply:
            </p>
            <ul className={UL}>
              <li><b>Payment receipts</b> &mdash; anonymised (transaction ID + amount,
                no PII) for <b>8 years</b> as required by the Income Tax Act, 1961
                and GST law.</li>
              <li><b>Deletion audit log</b> &mdash; a single record containing your
                email + timestamp + count of rows deleted, so we can prove the
                deletion happened during a future audit.</li>
              <li><b>Rolling backups</b> &mdash; fully scrubbed within 30 days of
                deletion.</li>
            </ul>
          </section>

          {/* ── 7. Account deletion ──────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>7. Account &amp; data deletion</h2>
            <p className={P}>
              You can permanently delete your account and all associated data at
              any time. Two paths:
            </p>
            <ul className={UL}>
              <li><b>Instant self-service</b> &mdash; sign in and visit{" "}
                <Link to="/account-deletion" className="text-amber-700 underline">
                  astrokuberchat.com/account-deletion
                </Link>{" "}
                &rarr; type &ldquo;DELETE&rdquo; &rarr; your data is wiped
                immediately.</li>
              <li><b>Email request</b> (for users who have lost access) &mdash; submit
                the form on the same page or email{" "}
                <a className="text-amber-700 underline" href="mailto:privacy@astrokuberchat.com">privacy@astrokuberchat.com</a>{" "}
                from any address. We will action verified requests within 7 days.</li>
            </ul>
            <p className={P}>
              The full list of what gets deleted versus what is retained for tax /
              audit compliance is documented on the deletion page itself.
            </p>
          </section>

          {/* ── 8. Your rights ───────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>8. Your rights (DPDP Act 2023, GDPR &amp; CCPA)</h2>
            <p className={P}>
              Depending on where you live, you may have one or more of the following
              rights:
            </p>
            <ul className={UL}>
              <li><b>Right to access</b> the personal data we hold about you.</li>
              <li><b>Right to correct</b> inaccurate or incomplete data.</li>
              <li><b>Right to delete</b> your account, chats and birth details (see
                Section 7).</li>
              <li><b>Right to withdraw consent</b> for specific processing (e.g.
                push notifications, marketing emails).</li>
              <li><b>Right to portability</b> &mdash; export your data in a structured,
                machine-readable JSON format.</li>
              <li><b>Right to opt-out of &ldquo;sale&rdquo; of personal information</b> (CCPA):
                We do not sell personal data and have never done so. You therefore do
                not need to file an opt-out request &mdash; but you may still confirm
                this in writing on demand.</li>
              <li><b>Right to lodge a complaint</b> with the Data Protection Board of
                India, the European Data Protection Board, the California Attorney
                General or your local supervisory authority.</li>
            </ul>
            <p className={P}>
              Email{" "}
              <a className="text-amber-700 underline" href="mailto:privacy@astrokuberchat.com">privacy@astrokuberchat.com</a>{" "}
              from your registered email address &mdash; we action verified requests
              within 30 days (7 days for deletion requests).
            </p>
          </section>

          {/* ── 9. Security ──────────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>9. Security</h2>
            <p className={P}>
              All traffic is encrypted in transit via TLS 1.3. Data at rest is
              encrypted using AES-256 inside MongoDB Atlas. Sensitive secrets
              (API keys, OAuth client secrets, JWT secrets) are stored in encrypted
              environment variables, never in source code. Session cookies are
              <code> HttpOnly</code>, <code>Secure</code>, <code>SameSite=None</code>{" "}
              and rotate every 7 days. Passwords (where used) are hashed with
              bcrypt cost factor 12. We perform regular vulnerability scans and
              monitor for unauthorized access.
            </p>
          </section>

          {/* ── 10. Children ─────────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>10. Children&rsquo;s privacy</h2>
            <p className={P}>
              The Service is intended for users <b>aged 18 and above</b>. We do not
              knowingly collect personal data from anyone under 18. If you believe
              a minor has registered, please email{" "}
              <a className="text-amber-700 underline" href="mailto:privacy@astrokuberchat.com">privacy@astrokuberchat.com</a>{" "}
              and we will delete the account and all related data within 7 days.
              We do not design any feature to appeal to children, and we do not
              show our app to under-13 users in Google Play&rsquo;s &ldquo;Designed
              for Families&rdquo; programme.
            </p>
          </section>

          {/* ── 11. International transfers ──────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>11. International transfers</h2>
            <p className={P}>
              Some of our sub-processors (Google, Anthropic, PayPal, Twilio, Resend)
              are based in the United States and other jurisdictions outside India.
              By using the Service you consent to your personal data being
              transferred to and processed in those jurisdictions, subject to
              Standard Contractual Clauses or equivalent safeguards. EU/EEA users:
              we rely on the EU&ndash;US Data Privacy Framework where applicable.
            </p>
          </section>

          {/* ── 12. Android-app specifics ────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>12. Android app (Google Play) specifics</h2>
            <p className={P}>
              The AstroKuberChat Android app is a Trusted Web Activity (TWA) wrapper
              around our website &mdash; meaning the data practices in this policy
              apply identically to the Android app. The app:
            </p>
            <ul className={UL}>
              <li>Does not collect any data through Android-specific permissions
                beyond what is listed in Section 2.</li>
              <li>Requests the <code>android.permission.INTERNET</code> permission
                only (required for the wrapper itself). All other access (storage,
                camera, microphone) is granted at the time you tap an Action button
                and can be revoked from Android Settings.</li>
              <li>Honours Google Play&rsquo;s Data Safety form &mdash; the
                declarations there match this policy line-for-line.</li>
              <li>Includes an external account-deletion URL (Section 7) as required
                by Google Play.</li>
            </ul>
          </section>

          {/* ── 12b. iOS-app specifics ───────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>12b. iOS app (Apple App Store) specifics</h2>
            <p className={P}>
              The AstroKuberChat iOS app is a Capacitor WebView wrapper around our
              website &mdash; meaning the data practices in this policy apply
              identically to the iOS app. The App Store Connect &ldquo;App
              Privacy&rdquo; nutrition label declares the following ten data types,
              all of them <b>linked to your identity</b> and <b>NOT used for
              tracking</b>:
            </p>
            <ul className={UL}>
              <li><b>Contact Info</b> &mdash; Name, Email Address, Phone Number
                (used for App Functionality and Product Personalization only).</li>
              <li><b>User Content</b> &mdash; Other User Content (your birth
                details and chat messages with astrologers, used for App
                Functionality and Product Personalization only).</li>
              <li><b>Identifiers</b> &mdash; User ID (internal account ID) and
                Device ID (APNs device token for push notifications).</li>
              <li><b>Purchases</b> &mdash; Purchase History (used for App
                Functionality and Analytics).</li>
              <li><b>Usage Data</b> &mdash; Product Interaction (used for Analytics
                and Product Personalization).</li>
              <li><b>Diagnostics</b> &mdash; Crash Data and Performance Data (used
                for App Functionality and Analytics).</li>
            </ul>
            <p className={P}>
              We do <b>not</b> use the Apple <code>IDFA</code> (Identifier for
              Advertisers). We do <b>not</b> present the App Tracking Transparency
              (ATT) prompt because we do not track you across apps or websites owned
              by other companies. All third-party SDKs bundled in the iOS app are
              limited to Capacitor plugins for camera, share, keyboard, splash-screen
              and push notifications; none of them perform cross-app tracking.
            </p>
            <p className={P}>
              iOS permissions we may request at the time you tap a feature:
            </p>
            <ul className={UL}>
              <li><code>Notifications</code> &mdash; only when you tap
                &ldquo;Enable notifications&rdquo;. Used to send booking alerts,
                astrologer-ready notifications and daily horoscope pushes.</li>
              <li><code>Camera</code> &mdash; only when you tap the palm-photo
                upload button in the Palmistry tool.</li>
              <li><code>Photo Library</code> &mdash; only when you tap
                &ldquo;Choose from library&rdquo; on the palm-photo upload
                screen.</li>
            </ul>
            <p className={P}>
              Consultations, per-minute chat billing, wallet recharges and premium
              tool unlocks in the iOS app relate to <b>real-world astrological
              services delivered by third-party independent astrologers outside the
              app</b> (voice calls via PSTN, human-to-human chat sessions and
              downloadable reports). Under Apple App Store Review Guideline
              <b> 3.1.3(e)</b> (Person-to-Person Services), payments for such
              services are processed through third-party payment gateways
              (Razorpay/PayPal) and Apple In-App Purchase is not required. Google
              Play&rsquo;s equivalent policy also applies to the Android app.
            </p>
            <p className={P}>
              Apple&rsquo;s privacy policy governs data Apple itself collects
              through the App Store and APNs and is independent of this policy.
              See <a className="text-amber-700 underline" href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer">apple.com/legal/privacy</a>.
            </p>
          </section>

          {/* ── 13. Changes ──────────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>13. Changes to this policy</h2>
            <p className={P}>
              We may update this Privacy Policy from time to time. Material changes
              will be announced in-app or via email at least 7 days before they take
              effect. The &ldquo;Last updated&rdquo; date at the top of this page
              will always reflect the most recent revision. Continued use of the
              Service after the change implies acceptance of the updated policy.
            </p>
          </section>

          {/* ── 14. Contact ──────────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>14. Contact</h2>
            <p className={P}>
              Data controller: <b>AstroKuberChat</b> (Sole Proprietorship), India.<br />
              Email: <a className="text-amber-700 underline" href="mailto:privacy@astrokuberchat.com">privacy@astrokuberchat.com</a><br />
              Account deletion:{" "}
              <Link to="/account-deletion" className="text-amber-700 underline">
                astrokuberchat.com/account-deletion
              </Link><br />
              Website: <a className="text-amber-700 underline" href="https://astrokuberchat.com">https://astrokuberchat.com</a><br />
              Google Play listing: <i>AstroKuberChat</i> (com.astrokuberchat.twa)<br />
              Apple App Store listing: <i>AstroKuberChat</i>
            </p>
          </section>

        </div>
      </main>
      <Footer />
    </div>
  );
}
